import React, { useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Img from './Img';
import JustifiedGrid from './JustifiedGrid';
import Viewer from './Viewer';

const VIEW_KEY = 'jc:photo-view';

const readView = () => {
  try {
    return window.localStorage.getItem(VIEW_KEY) === 'single' ? 'single' : 'grid';
  } catch {
    return 'grid';
  }
};

const pad = (n) => String(n).padStart(3, '0');

/**
 * A set of photos with a Grid / Single view toggle and the full-screen
 * viewer. The open photo lives in `?photo=<stem>` so the back button
 * closes the viewer and photos can be linked to directly.
 */
const PhotoSet = ({ images, title, altFor, label = 'Photographs', id }) => {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [view, setView] = useState(readView);

  const photo = params.get('photo');
  const index = photo ? images.findIndex((img) => img.stem === photo) : -1;

  const open = (i) => setParams({ photo: images[i].stem }, { state: { viewer: true } });
  const move = (i) => setParams({ photo: images[i].stem }, { replace: true, state: location.state });
  const close = () => {
    if (location.state?.viewer) navigate(-1);
    else setParams({}, { replace: true });
  };

  const choose = (next) => {
    setView(next);
    try {
      window.localStorage.setItem(VIEW_KEY, next);
    } catch {
      // Private mode — the choice just won't persist.
    }
  };

  return (
    <section className="photoset" id={id} aria-label={`${title} photographs`}>
      <header className="photoset__head">
        <span className="label">
          {label} <span className="label__count">{images.length}</span>
        </span>
        <div className="toggle" role="group" aria-label="Layout">
          {['grid', 'single'].map((v) => (
            <button
              key={v}
              type="button"
              className={`toggle__btn${view === v ? ' is-active' : ''}`}
              aria-pressed={view === v}
              onClick={() => choose(v)}
            >
              {v === 'grid' ? 'Grid' : 'Single'}
            </button>
          ))}
        </div>
      </header>

      {view === 'grid' ? (
        <JustifiedGrid images={images} onOpen={open} altFor={altFor} />
      ) : (
        <div className="pcol">
          {images.map((image, i) => (
            <figure key={image.src} className="pcol__item" style={{ '--a': image.aspect }}>
              <button type="button" className="pcol__open" onClick={() => open(i)} aria-label={`Open photo ${i + 1}`}>
                <Img image={image} alt={altFor(image, i)} sizes="(max-width: 767px) 100vw, 1000px" />
              </button>
              <figcaption className="pcol__caption">
                <span className="pcol__num">{pad(i + 1)}</span>
                {image.caption && <span>{image.caption}</span>}
              </figcaption>
            </figure>
          ))}
        </div>
      )}

      {index >= 0 && (
        <Viewer images={images} index={index} onIndex={move} onClose={close} title={title} altFor={altFor} />
      )}
    </section>
  );
};

export default PhotoSet;
